import React, { createContext, useContext, useState, useEffect } from 'react';
import useInactivityTimer from '../hooks/useInactivityTimer';
import googleTokenManager from '../utils/googleTokenManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Hook para detectar inactividad real - CON MODO TEST
  const testMode = localStorage.getItem('testInactivity') === 'true';
  const inactivityTime = testMode ? 30000 : 7200000; // 30 segundos en test, 2 horas normal
  
  const { resetTimer } = useInactivityTimer(() => {
    console.log(`🚪 Logout por inactividad de ${testMode ? '30 segundos (TEST)' : '2 horas'}`);
    logout();
    window.location.href = '/';
  }, inactivityTime);

  useEffect(() => {
    // Verificar si hay un token guardado al cargar la aplicación
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');
    const refreshToken = localStorage.getItem('googleRefreshToken');
    
    if (savedToken && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setToken(savedToken);
      setUser(parsedUser);
      
      // Iniciar refresh automático de Google token si existe
      if (refreshToken) {
        googleTokenManager.scheduleTokenRefresh(refreshToken);
      }
    }
    
    // Listener para logout forzado (cuando falla refresh)
    const handleForceLogout = () => {
      logout();
      window.location.href = '/';
    };
    
    window.addEventListener('forceLogout', handleForceLogout);
    
    setLoading(false);
    
    return () => {
      window.removeEventListener('forceLogout', handleForceLogout);
    };
  }, []);

  // Función para manejar login con Google (incluyendo refresh token)
  const handleGoogleLogin = (googleData, refreshToken = null) => {
    // Guardar refresh token si se proporciona
    if (refreshToken) {
      localStorage.setItem('googleRefreshToken', refreshToken);
      // Programar refresh automático
      googleTokenManager.scheduleTokenRefresh(refreshToken);
    }
    
    // Guardar access token de Google
    if (googleData.access_token) {
      localStorage.setItem('googleAccessToken', googleData.access_token);
    }
  };

  const login = (userData, authToken, googleData = null, refreshToken = null) => {
    // Asegurar que userData tenga la estructura correcta
    const processedUserData = {
      id: userData.id,
      name: userData.nombre || userData.name,
      email: userData.correo || userData.email,
      dependencia: userData.dependencia,
      picture: userData.picture
    };
    
    // Guardar primero en localStorage antes de actualizar el estado
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authUser', JSON.stringify(processedUserData));
    
    setUser(processedUserData);
    setToken(authToken);
    
    // Manejar datos de Google si se proporcionan
    if (googleData || refreshToken) {
      handleGoogleLogin(googleData || {}, refreshToken);
    }
    
    // Resetear timer de inactividad
    resetTimer();
  };

  const logout = () => {
    // Limpiar timers de Google
    googleTokenManager.clearRefreshTimer();
    
    // Limpiar datos de sesión
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('googleRefreshToken');
    localStorage.removeItem('googleAccessToken');
  };

  const isAuthenticated = () => {
    // Verificar tanto el estado como localStorage para mayor confiabilidad
    const hasToken = !!token || !!localStorage.getItem('authToken');
    const hasUser = !!user || !!localStorage.getItem('authUser');
    return hasToken && hasUser;
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated,
    loading,
    resetInactivityTimer: resetTimer
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};